import { useEffect, useRef } from "react";

/**
 * Runs when the browser comes back to a page it had left - the back button
 * after a redirect out to Google, most of the time.
 *
 * A screen that sends the browser away leaves its submitting flag set, because
 * nothing after the redirect runs. Returning does not necessarily undo that:
 * a page served from the back/forward cache is restored with its React state
 * exactly as it was, so the button it disabled goes on spinning at a user who
 * has no way to get it back.
 *
 * `pageshow` is what the cache announces itself with. It fires on an ordinary
 * load too, where the screen has already mounted fresh and resetting is a
 * no-op, so the `persisted` flag is not worth branching on.
 */
export function useRestoreFromRedirect(onRestore: () => void): void {
  // Held in a ref so an inline callback does not resubscribe every render.
  const latest = useRef(onRestore);
  latest.current = onRestore;

  useEffect(() => {
    const handle = () => latest.current();
    window.addEventListener("pageshow", handle);
    return () => window.removeEventListener("pageshow", handle);
  }, []);
}
